import { getField, updateField } from 'vuex-map-fields';
import AudioForegroundService from '~services/AudioForegroundService';
import BackgroundAudioBridge from '~services/BackgroundAudioBridge';
import PictureInPicture from '~services/PictureInPicture';

/**
 * Global audio-playback state so a meditation keeps playing when the user
 * navigates away from the session page, and so a persistent mini-player can
 * render at the app-shell level.
 *
 * The actual `Playerjs` instance is held non-reactively on the module so
 * mini-player commands (play/pause/stop) can be dispatched without passing
 * refs down the tree. Vuex wraps state in a Proxy which would break the
 * third-party player, so we stash it outside `state`.
 */
let playerInstance = null;
let mediaSessionHandlersBound = false;

function applyInstance(action) {
    if (!playerInstance) return;
    try {
        playerInstance.api(action);
    } catch (_) {
        /* player was torn down mid-call */
    }
}

/**
 * Web MediaSession integration — paints play/pause/seek controls on the
 * iOS Lock Screen and the Android lock-screen "now playing" widget. The
 * Web MediaSession API has been supported in WKWebView since iOS 15 and
 * in Chromium WebView since 89.
 *
 * Without this, even with `UIBackgroundModes=audio` and AVAudioSession
 * configured the OS shows no media controls — and on Android the
 * foreground-service notification shows just a static "playing…" tile
 * with no buttons. Wiring the media session promotes that to a rich
 * media notification with real transport controls.
 */
function bindMediaSession({ title, artwork }) {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) {
        return;
    }
    try {
        navigator.mediaSession.metadata = new window.MediaMetadata({
            title: title || 'Meditation',
            artist: 'Chill Anywhere',
            album: 'Chill Anywhere',
            artwork: artwork
                ? [
                      { src: artwork, sizes: '512x512', type: 'image/png' },
                      { src: artwork, sizes: '256x256', type: 'image/png' }
                  ]
                : []
        });
    } catch (_) {
        /* MediaMetadata constructor not available */
    }

    if (mediaSessionHandlersBound) return;
    try {
        navigator.mediaSession.setActionHandler('play', () =>
            applyInstance('play')
        );
        navigator.mediaSession.setActionHandler('pause', () =>
            applyInstance('pause')
        );
        navigator.mediaSession.setActionHandler('stop', () =>
            applyInstance('stop')
        );
        // Seek-back / seek-forward give a 10-second skip, matching what
        // most podcast / meditation apps surface on the Lock Screen.
        navigator.mediaSession.setActionHandler('seekbackward', event => {
            const offset = event && event.seekOffset ? event.seekOffset : 10;
            try {
                const v = document.querySelector('#videoPlayer video');
                if (v) v.currentTime = Math.max(0, v.currentTime - offset);
            } catch (_) {
                /* no video yet */
            }
        });
        navigator.mediaSession.setActionHandler('seekforward', event => {
            const offset = event && event.seekOffset ? event.seekOffset : 10;
            try {
                const v = document.querySelector('#videoPlayer video');
                if (v) v.currentTime = v.currentTime + offset;
            } catch (_) {
                /* no video yet */
            }
        });
        mediaSessionHandlersBound = true;
    } catch (_) {
        /* setActionHandler not supported on this OS */
    }
}

function setMediaSessionState(isPlaying) {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) {
        return;
    }
    try {
        navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    } catch (_) {
        /* older WebKit lacks this property */
    }
}

function clearMediaSession() {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) {
        return;
    }
    try {
        navigator.mediaSession.metadata = null;
        navigator.mediaSession.playbackState = 'none';
    } catch (_) {
        /* non-fatal */
    }
}

const state = () => ({
    sessionId: null,
    sessionTitle: null,
    sessionThumbnail: null,
    sessionRoute: null,
    duration: 0,
    currentTime: 0,
    isPlaying: false,
    // Set to `true` once any playback has started; lets the mini-player
    // avoid showing for routes the user merely previewed.
    hasStarted: false
});

const getters = {
    getField,
    hasActiveSession: s => !!s.sessionId,
    isOnSessionRoute: (s, _g, rootState) => {
        const route = rootState.route;
        if (!route || !s.sessionRoute) return false;
        return (
            route.path === s.sessionRoute ||
            route.name === 'dashboard.sessions.view'
        );
    }
};

const mutations = {
    updateField,
    SET_SESSION(state, payload) {
        state.sessionId = payload.sessionId;
        state.sessionTitle = payload.sessionTitle;
        state.sessionThumbnail = payload.sessionThumbnail;
        state.sessionRoute = payload.sessionRoute;
        state.duration = payload.duration || 0;
        state.currentTime = 0;
        state.isPlaying = false;
        state.hasStarted = false;
    },
    UPDATE_TIME(state, { currentTime, duration }) {
        if (typeof currentTime === 'number') state.currentTime = currentTime;
        if (typeof duration === 'number' && duration > 0)
            state.duration = duration;
    },
    SET_PLAYING(state, isPlaying) {
        state.isPlaying = !!isPlaying;
        if (isPlaying) state.hasStarted = true;
    },
    CLEAR(state) {
        state.sessionId = null;
        state.sessionTitle = null;
        state.sessionThumbnail = null;
        state.sessionRoute = null;
        state.duration = 0;
        state.currentTime = 0;
        state.isPlaying = false;
        state.hasStarted = false;
    }
};

const actions = {
    register({ commit }, { player, sessionInfo }) {
        playerInstance = player;
        commit('SET_SESSION', sessionInfo);
        bindMediaSession({
            title: sessionInfo && sessionInfo.sessionTitle,
            artwork: sessionInfo && sessionInfo.sessionThumbnail
        });
        // iOS-only: arm the native AVPlayer handoff so audio survives
        // the WKWebView ProcessAssertion expiry when the user backgrounds.
        BackgroundAudioBridge.attach({
            getState: () => ({
                title: sessionInfo && sessionInfo.sessionTitle,
                artwork: sessionInfo && sessionInfo.sessionThumbnail
            })
        });
    },
    updateTime({ commit }, payload) {
        commit('UPDATE_TIME', payload);
    },
    setPlaying({ commit, state }, isPlaying) {
        commit('SET_PLAYING', isPlaying);
        PictureInPicture.setEligible(isPlaying);
        setMediaSessionState(isPlaying);
        if (isPlaying) {
            AudioForegroundService.start({
                title: state.sessionTitle || 'Meditation playing'
            });
        } else {
            AudioForegroundService.stop();
        }
    },
    play() {
        applyInstance('play');
    },
    pause() {
        applyInstance('pause');
    },
    stop({ commit }) {
        // Playerjs's `stop` only rewinds on some platforms — follow up
        // with `pause` and detach the underlying <video>'s src so the
        // WKWebView/Chromium media pipeline actually releases the
        // audio buffer. Without this, previously-played audio can
        // bleed into the next session on iOS.
        applyInstance('pause');
        applyInstance('stop');
        try {
            const videoEls = document.querySelectorAll(
                '#videoPlayer video, .videoPlayer video'
            );
            videoEls.forEach(v => {
                try {
                    v.pause();
                    v.removeAttribute('src');
                    v.load();
                } catch (_) {
                    /* non-fatal */
                }
            });
        } catch (_) {
            /* non-fatal */
        }
        playerInstance = null;
        AudioForegroundService.stop();
        BackgroundAudioBridge.detach();
        PictureInPicture.setEligible(false);
        clearMediaSession();
        commit('CLEAR');
    },
    detach({ commit }) {
        // Called when the session component is truly destroyed (not just
        // keep-alive deactivation) — release the ref without touching the
        // remote player, which is already gone.
        playerInstance = null;
        AudioForegroundService.stop();
        BackgroundAudioBridge.detach();
        PictureInPicture.setEligible(false);
        clearMediaSession();
        commit('CLEAR');
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
